import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import type {
  EligibilityOutcomeView,
  EligibilityReasonView,
  MatchEligibilityView,
  RosterStatus,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import { ManualCheckItem } from './ManualCheckItem';
import { rosterRoleKeys } from './matchRoster';
import {
  eligibilityOutcomeColors,
  eligibilityOutcomeKeys,
  eligibilityPath,
  formatCheckedAt,
  reasonText,
  reasonsInOrder,
} from './rosterEligibility';

export interface RosterEligibilitySectionProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  matchId: string;
  /** The roster version shown above; a change reloads the stored result. */
  rosterVersion: number;
  /** Finalizing or reopening changes what can be done without changing the version. */
  rosterStatus: RosterStatus;
}

function OutcomeChip({
  locale,
  outcome,
}: {
  locale: SupportedLocale;
  outcome: EligibilityOutcomeView;
}): React.JSX.Element {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={eligibilityOutcomeColors[outcome]}
      label={translate(locale, eligibilityOutcomeKeys[outcome])}
    />
  );
}

function ReasonList({
  locale,
  reasons,
}: {
  locale: SupportedLocale;
  reasons: readonly EligibilityReasonView[];
}): React.JSX.Element | null {
  if (reasons.length === 0) return null;
  return (
    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
      {reasonsInOrder(reasons).map((reason, index) => (
        <Typography component="li" variant="body2" key={`${reason.code}-${String(index)}`}>
          {reasonText(locale, reason)}
          {reason.sourceClause !== null &&
            ` (${translate(locale, 'eligibility.source')}: ${reason.sourceClause})`}
          {reason.resolvedByException !== null &&
            ` · ${translate(locale, 'eligibility.exception').replace('{reference}', reason.resolvedByException)}`}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * The stored eligibility check of a match roster. A registrar checks the roster version shown; every
 * athlete and the roster as a whole get an outcome with all reasons, written out and never by colour
 * alone. A result whose inputs, roster or rule version changed since is marked out of date.
 */
export function RosterEligibilitySection({
  locale,
  base,
  csrfToken,
  matchId,
  rosterVersion,
  rosterStatus,
}: RosterEligibilitySectionProps): React.JSX.Element {
  const [view, setView] = useState<MatchEligibilityView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setView(await apiRequest<MatchEligibilityView>(eligibilityPath(base, matchId)));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, matchId]);

  useEffect(() => {
    setErrorKey(null);
    setChecked(false);
    void load();
  }, [load, rosterVersion, rosterStatus]);

  async function evaluate(): Promise<void> {
    if (view === null) return;
    setBusy(true);
    setErrorKey(null);
    setChecked(false);
    try {
      setView(
        await apiRequest<MatchEligibilityView>(eligibilityPath(base, matchId), {
          method: 'POST',
          csrfToken,
          ifMatchVersion: view.rosterVersion,
        }),
      );
      setChecked(true);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      await load();
    } finally {
      setBusy(false);
    }
  }

  const evaluation = view?.evaluation ?? null;
  const outOfDate =
    view !== null &&
    (view.rosterChanged ||
      view.rulesChanged ||
      (evaluation?.decisions.some((decision) => decision.stale) ?? false));

  return (
    <Box
      component="section"
      aria-label={translate(locale, 'eligibility.title')}
      sx={{ mt: 2, mb: 2 }}
    >
      <Typography variant="subtitle2" component="h5">
        {translate(locale, 'eligibility.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {translate(locale, 'eligibility.hint')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {checked && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, 'eligibility.evaluate.done')}
        </Alert>
      )}
      {view === null && errorKey === null && (
        <Typography variant="body2">{translate(locale, 'eligibility.loading')}</Typography>
      )}
      {view !== null && evaluation === null && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {translate(locale, 'eligibility.none')}
        </Typography>
      )}
      {view !== null && outOfDate && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {view.rosterChanged && <div>{translate(locale, 'eligibility.rosterChanged')}</div>}
          {view.rulesChanged && <div>{translate(locale, 'eligibility.rulesChanged')}</div>}
          {!view.rosterChanged && !view.rulesChanged && translate(locale, 'eligibility.stale')}
        </Alert>
      )}
      {evaluation !== null && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {(evaluation.ruleProfile === null
              ? translate(locale, 'eligibility.checkedWithoutRules')
              : translate(locale, 'eligibility.checked').replace(
                  '{version}',
                  String(evaluation.ruleProfile.versionNumber),
                )
            )
              .replace('{checkedAt}', formatCheckedAt(evaluation.evaluatedAt, locale))
              .replace('{matchDay}', formatCalendarDate(evaluation.evaluatedOn, locale))}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" component="span">
              {translate(locale, 'eligibility.roster')}
            </Typography>
            <OutcomeChip locale={locale} outcome={evaluation.outcome} />
          </Box>
          <ReasonList locale={locale} reasons={evaluation.reasons} />
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, mt: 1.5, mb: 0 }}>
            {evaluation.decisions.map((decision) => (
              <Box component="li" key={decision.athlete.id}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" component="span">
                    {athleteDisplayName(decision.athlete, locale)} ·{' '}
                    {translate(locale, rosterRoleKeys[decision.role])}
                  </Typography>
                  <OutcomeChip locale={locale} outcome={decision.outcome} />
                  {!decision.onRoster && (
                    <Typography variant="body2" component="span" color="text.secondary">
                      {translate(locale, 'eligibility.removed')}
                    </Typography>
                  )}
                  {decision.onRoster && decision.stale && (
                    <Typography variant="body2" component="span" color="text.secondary">
                      {translate(locale, 'eligibility.decisionStale')}
                    </Typography>
                  )}
                </Box>
                <ReasonList locale={locale} reasons={decision.reasons} />
              </Box>
            ))}
          </Stack>
          {evaluation.hiddenEntryCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {translate(locale, 'eligibility.hidden').replace(
                '{count}',
                String(evaluation.hiddenEntryCount),
              )}
            </Typography>
          )}
        </Box>
      )}
      {view !== null && view.mandatoryManualChecks.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">{translate(locale, 'eligibility.manualChecks')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'manualCheck.hint')}
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
            {view.mandatoryManualChecks.map((check) => (
              <ManualCheckItem
                key={check.key}
                locale={locale}
                base={base}
                csrfToken={csrfToken}
                matchId={matchId}
                check={check}
                canRecord={view.canRecordManualChecks}
                onChanged={load}
              />
            ))}
          </Box>
        </Box>
      )}
      {view?.canEvaluate === true && (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => {
            void evaluate();
          }}
        >
          {translate(
            locale,
            evaluation === null ? 'eligibility.evaluate' : 'eligibility.evaluate.again',
          )}
        </Button>
      )}
    </Box>
  );
}
